import { Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatLeadTime } from './lead-time';
import { leadStyleShortKey, type LeadClimbStyle } from './lead-climb-style';
import { cn } from '../../lib/utils';

interface LeadCardSummaryProps {
  /** Human format: "17+" or "17". Null when there is no card. */
  holds: string | null | undefined;
  /** Seconds, thousandths in the decimals. */
  timeSeconds: number | null | undefined;
  /**
   * The time already formatted by the server, when there is one. Preferred over
   * reformatting, so one card never reads two ways on the same page.
   */
  formattedTime?: string | null;
  /** The last hold on the route — the card is a top. */
  topped?: boolean;
  /**
   * On top rope, or leading it. Shown because it is what the card's place is
   * decided by first: a top roped 30 is behind a led 1, and a sheet that does
   * not say so reads as a mistake.
   */
  climbedAs?: LeadClimbStyle | null;
  /** Names the route, for a summary that stands on its own in a list. */
  label?: string | null;
  className?: string;
}

/**
 * A lead card read back: how far up, and how long. `17+ · 2:05.500`.
 *
 * The hold is the number that decides the route and the time only separates
 * equals, so the hold is the one set in the heavier type — on a board of fifty
 * cards that is the column a finger runs down.
 *
 * A **top roped** card is marked, and a led one is not: leading is the ordinary
 * way a lead route is climbed, and the thing a reader has to notice is the card
 * that is behind every led one whatever its number.
 */
export function LeadCardSummary({
  holds,
  timeSeconds,
  formattedTime,
  topped = false,
  climbedAs,
  label,
  className,
}: LeadCardSummaryProps) {
  const { t } = useTranslation();

  if (holds == null || holds === '') {
    return <span className={cn('text-muted-foreground', className)}>—</span>;
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5 tabular-nums', className)}>
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
      <span
        className={cn('font-semibold', topped && 'text-green-600')}
        data-testid="lead-summary-holds"
      >
        {holds}
      </span>
      {topped && <Trophy className="size-3 text-green-600" aria-hidden />}
      {climbedAs === 'top_rope' && (
        <span
          className="rounded border border-amber-600/60 px-1 text-[10px] font-semibold uppercase leading-4 text-amber-700 dark:text-amber-500"
          title={t('leadRounds.style.top_rope')}
          data-testid="lead-summary-style"
        >
          {t(leadStyleShortKey('top_rope'))}
        </span>
      )}
      <span className="text-xs text-muted-foreground" data-testid="lead-summary-time">
        {formattedTime ?? formatLeadTime(timeSeconds ?? null)}
      </span>
    </span>
  );
}
